/**
 * База знаний: пространства, дерево страниц и сама страница.
 *
 * Сервис knowledge отдаёт дерево одним запросом плоским списком с родителями —
 * собирать его в узлы дешевле на клиенте, чем ходить за каждым уровнем.
 */
import { pageApi, spaceApi } from '@/api'

import type {
  V1Page,
  V1PageBrief,
  V1PageIssueLink,
  V1PageVersion,
} from '@/api/clients/knowledge-page'
import type { V1Space } from '@/api/clients/knowledge-space'

export interface Space {
  id: string
  key: string
  name: string
  description: string
  pageCount: number
}

export interface PageBrief {
  id: string
  parentId: string
  title: string
  position: number
  hasChildren: boolean
}

/** Узел дерева: страница и её дети */
export interface PageNode extends PageBrief {
  children: PageNode[]
}

export interface Page {
  id: string
  spaceId: string
  parentId: string
  title: string
  contentMd: string
  createdBy: string
  updatedBy: string
  updatedAt: string
  version: number
}

export interface PageIssueLink {
  id: string
  issueId: string
  issueKey: string
  issueTitle: string
}

/** Снимок страницы: версии заводит человек кнопкой, автосейв их не плодит. */
export interface PageVersion {
  id: string
  number: number
  title: string
  contentMd: string
  comment: string
  createdBy: string
  createdAt: string
}

const spaceOf = (space: V1Space): Space => ({
  id: space.id ?? '',
  key: space.key ?? '',
  name: space.name ?? '',
  description: space.description ?? '',
  pageCount: Number(space.pageCount ?? 0),
})

const briefOf = (page: V1PageBrief): PageBrief => ({
  id: page.id ?? '',
  parentId: page.parentId ?? '',
  title: page.title ?? '',
  position: Number(page.position ?? 0),
  hasChildren: Boolean(page.hasChildren),
})

const pageOf = (page: V1Page): Page => ({
  id: page.id ?? '',
  spaceId: page.spaceId ?? '',
  parentId: page.parentId ?? '',
  title: page.title ?? '',
  contentMd: page.contentMd ?? '',
  createdBy: page.createdBy ?? '',
  updatedBy: page.updatedBy ?? '',
  updatedAt: page.updatedAt ?? '',
  version: Number(page.version ?? 0),
})

const versionOf = (version: V1PageVersion): PageVersion => ({
  id: version.id ?? '',
  number: Number(version.number ?? 0),
  title: version.title ?? '',
  contentMd: version.contentMd ?? '',
  comment: version.comment ?? '',
  createdBy: version.createdBy ?? '',
  createdAt: version.createdAt ?? '',
})

const linkOf = (link: V1PageIssueLink): PageIssueLink => ({
  id: link.id ?? '',
  issueId: link.issueId ?? '',
  issueKey: link.issueKey ?? '',
  issueTitle: link.issueTitle ?? '',
})

export const listSpaces = async (): Promise<Space[]> => {
  const { data } = await spaceApi.spaceServiceListSpaces()

  return (data.spaces ?? []).map(spaceOf)
}

/** Ключ пространства — заглавные латинские и цифры: сервис строчные не свернёт. */
export const SPACE_KEY = /^[A-Z][A-Z0-9]{1,15}$/

export const createSpace = async (key: string, name: string): Promise<Space> => {
  const { data } = await spaceApi.spaceServiceCreateSpace({ key, name })

  return spaceOf(data.space ?? {})
}

/**
 * Один уровень дерева: дети указанного родителя, а без родителя — корни
 * пространства. Целиком дерево не запрашивается намеренно: в пространстве
 * сотни страниц, а сайдбару за раз нужен один уровень.
 */
export const getTree = async (spaceId: string, parentId?: string): Promise<PageBrief[]> => {
  const { data } = await pageApi.pageServiceGetPageTree(spaceId, parentId)

  return (data.pages ?? []).map(briefOf)
}

export const getPage = async (pageId: string): Promise<Page> => {
  const { data } = await pageApi.pageServiceGetPage(pageId)

  return pageOf(data.page ?? {})
}

export const listIssueLinks = async (pageId: string): Promise<PageIssueLink[]> => {
  const { data } = await pageApi.pageServiceListIssueLinks(pageId)

  return (data.links ?? []).map(linkOf)
}

export const createPage = async (
  spaceId: string,
  parentId: string,
  title: string,
): Promise<Page> => {
  const { data } = await pageApi.pageServiceCreatePage({ spaceId, parentId, title, contentMd: '' })

  return pageOf(data.page ?? {})
}

/**
 * Правка страницы. Ею же пишет автосейв: версию этот вызов не создаёт —
 * снимок делает только `saveVersion`.
 */
export const updatePage = async (id: string, title: string, contentMd: string): Promise<Page> => {
  const { data } = await pageApi.pageServiceUpdatePage(id, { title, contentMd })

  return pageOf(data.page ?? {})
}

export const movePage = async (id: string, parentId: string): Promise<void> => {
  await pageApi.pageServiceMovePage(id, { parentId })
}

export const deletePage = async (id: string): Promise<void> => {
  await pageApi.pageServiceDeletePage(id)
}

/** Найденная страница: заголовок и кусок текста вокруг совпадения. */
export interface PageHit {
  id: string
  spaceKey: string
  title: string
  snippet: string
}

export const searchPages = async (query: string, limit = 8): Promise<PageHit[]> => {
  const { data } = await pageApi.pageServiceSearchPages(query, undefined, '1', String(limit))

  return (data.results ?? []).map((hit) => ({
    id: hit.id ?? '',
    spaceKey: hit.spaceKey ?? '',
    title: hit.title ?? '',
    snippet: hit.snippet ?? '',
  }))
}

/** Страницы, привязанные к задаче — те же связи, прочитанные с другого конца. */
export const listIssuePages = async (issueKey: string): Promise<PageBrief[]> => {
  const { data } = await pageApi.pageServiceListIssuePages(issueKey)

  return (data.pages ?? []).map(briefOf)
}

export const linkIssue = async (pageId: string, issueKey: string): Promise<PageIssueLink> => {
  const { data } = await pageApi.pageServiceLinkIssue(pageId, { issueKey })

  return linkOf(data.link ?? {})
}

export const unlinkIssue = async (pageId: string, issueKey: string): Promise<void> => {
  await pageApi.pageServiceUnlinkIssue(pageId, issueKey)
}

export const listVersions = async (pageId: string): Promise<PageVersion[]> => {
  const { data } = await pageApi.pageServiceListPageVersions(pageId, '1', '50')

  return (data.versions ?? []).map(versionOf)
}

export const saveVersion = async (pageId: string, comment: string): Promise<PageVersion> => {
  const { data } = await pageApi.pageServiceSavePageVersion(pageId, { comment })

  return versionOf(data.version ?? {})
}

export const restoreVersion = async (pageId: string, number: number): Promise<Page> => {
  const { data } = await pageApi.pageServiceRestorePageVersion(pageId, String(number), {})

  return pageOf(data.page ?? {})
}

/**
 * Страница и всё, что под ней. Нужна переносу: сделать страницу потомком
 * собственного потомка значит оторвать всё поддерево — сервис такой перенос
 * отклонит, но предлагать его в списке родителей незачем.
 */
export const withDescendants = (pages: PageBrief[], id: string): Set<string> => {
  const kids = new Map<string, string[]>()

  for (const page of pages) {
    kids.set(page.parentId, [...(kids.get(page.parentId) ?? []), page.id])
  }

  const marked = new Set<string>()
  const queue = [id]

  while (queue.length) {
    const current = queue.shift() as string

    if (marked.has(current)) continue

    marked.add(current)
    queue.push(...(kids.get(current) ?? []))
  }

  return marked
}

/**
 * Дерево из плоского списка. Страница, чей родитель в выдачу не попал (его
 * удалили или он в другом пространстве), поднимается в корень: потерять её
 * молча хуже, чем показать не на своём уровне.
 */
export const toTree = (pages: PageBrief[]): PageNode[] => {
  const nodes = new Map<string, PageNode>(
    pages.map((page) => [page.id, { ...page, children: [] }]),
  )
  const roots: PageNode[] = []

  for (const node of nodes.values()) {
    const parent = node.parentId ? nodes.get(node.parentId) : undefined

    if (parent) parent.children.push(node)
    else roots.push(node)
  }

  const sort = (list: PageNode[]) => {
    list.sort((a, b) => a.position - b.position || a.title.localeCompare(b.title))
    list.forEach((node) => sort(node.children))
  }

  sort(roots)

  return roots
}

/** Путь до страницы — им подписывают крошки над заголовком. */
export const pathTo = (pages: PageBrief[], pageId: string): PageBrief[] => {
  const byId = new Map(pages.map((page) => [page.id, page]))
  const path: PageBrief[] = []

  let current = byId.get(pageId)

  while (current) {
    path.unshift(current)
    current = current.parentId ? byId.get(current.parentId) : undefined
    // Цикл в родителях положил бы вкладку: страница не может быть своим предком.
    if (path.length > 20) break
  }

  return path
}
